import React, { useState, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, RoundedBox } from '@react-three/drei';
import * as THREE from 'three';
import { LayerWithVisualization } from '../../types/modelBuilder';
import { LAYER_TEMPLATES } from '../../utils/layerUtils';
import { useModelBuilder } from '../../context/ModelBuilderContext';
import '../../styles/modelBuilder3d.css';

// Layer color mapping for arcade style
const LAYER_COLORS: Record<string, string> = {
  input: '#FF6B6B',      // Red
  dense: '#4ECDC4',      // Teal
  conv2d: '#45B7D1',     // Blue
  conv3d: '#96CEB4',     // Green
  batchnorm: '#FFEAA7',  // Yellow
  dropout: '#DDA0DD',    // Plum
  maxpool: '#FF8C94',    // Salmon
  avgpool: '#FFA07A',    // Light Salmon
  flatten: '#B4A7D6',    // Purple
  rnn: '#73A580',        // Sage
  lstm: '#6C5CE7',       // Violet
  gru: '#A29BFE',        // Lavender
  output: '#00B894',     // Green
  custom: '#74B9FF',     // Light Blue
};

// Blob node component
interface BlobNodeProps {
  layer: LayerWithVisualization;
  isSelected: boolean;
  onSelect: () => void;
  onDragStart: (e: THREE.Event) => void;
}

const BlobNode: React.FC<BlobNodeProps> = ({ layer, isSelected, onSelect, onDragStart }) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [hovered, setHovered] = useState(false);

  useFrame(() => {
    if (!meshRef.current) return;

    // Glow effect when selected
    if (isSelected && meshRef.current.material instanceof THREE.MeshPhongMaterial) {
      meshRef.current.material.emissive.setHex(0xffffff);
      meshRef.current.material.emissiveIntensity = 0.3;
    } else if (meshRef.current.material instanceof THREE.MeshPhongMaterial) {
      meshRef.current.material.emissive.setHex(0x000000);
      meshRef.current.material.emissiveIntensity = 0;
    }
  });

  const color = LAYER_COLORS[layer.type] || LAYER_COLORS.custom;

  // Determine blob size based on layer configuration
  let scale = 1;
  if (layer.out_features) {
    scale = Math.min(2, 0.5 + layer.out_features / 256);
  }
  if (layer.out_channels) {
    scale = Math.min(2, 0.5 + layer.out_channels / 64);
  }

  return (
    <group position={[layer.position.x / 100, layer.position.y / 100, layer.position.z]}>
      <RoundedBox
        ref={meshRef}
        args={[1.2 * scale, 1 * scale, 0.8 * scale]}
        radius={0.1}
        onPointerEnter={() => setHovered(true)}
        onPointerLeave={() => setHovered(false)}
        onClick={onSelect}
        onPointerDown={() => setIsDragging(true)}
        onPointerUp={() => setIsDragging(false)}
      >
        <meshPhongMaterial
          color={color}
          emissive={isSelected ? color : 0x000000}
          emissiveIntensity={isSelected ? 0.4 : 0}
          shininess={100}
        />
      </RoundedBox>


      {/* Label */}
      <sprite position={[0, (0.5 * scale) + 0.3, 0]}>
        <spriteMaterial
          map={createTextTexture((layer.name || layer.id).substring(0, 10), color)}
          sizeAttenuation
        />
      </sprite>
    </group>
  );
};

// Utility to create text texture for labels
function createTextTexture(text: string, color: string): THREE.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 64;
  const context = canvas.getContext('2d')!;

  context.fillStyle = 'transparent';
  context.fillRect(0, 0, 256, 64);

  context.fillStyle = color;
  context.font = 'Bold 32px Arial';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(text, 128, 32);

  const texture = new THREE.CanvasTexture(canvas);
  return texture;
}

// Helper component for connection lines
interface BufferGeometryLineProps {
  start: [number, number, number];
  end: [number, number, number];
}

const BufferGeometryLine: React.FC<BufferGeometryLineProps> = ({ start, end }) => {
  const geometry = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(...start),
    new THREE.Vector3(...end),
  ]);

  return <primitive attach="geometry" object={geometry} />;
};

// Main 3D Scene Component
interface Blob3DViewProps {
  layers: LayerWithVisualization[];
  selectedLayerId: string | null;
  onSelectLayer: (id: string) => void;
}

const Scene: React.FC<Blob3DViewProps> = ({ layers, selectedLayerId, onSelectLayer }) => {
  const { connectLayers } = useModelBuilder();
  const controlsRef = useRef<any>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera>(null);

  // Auto-adjust camera to fit all layers in view
  useFrame(() => {
    if (layers.length > 0 && cameraRef.current && controlsRef.current) {
      // Calculate bounding box of all layers
      let minX = Infinity, maxX = -Infinity;
      let minY = Infinity, maxY = -Infinity;
      let minZ = Infinity, maxZ = -Infinity;

      layers.forEach(layer => {
        const x = layer.position.x / 100;
        const y = layer.position.y / 100;
        const z = layer.position.z;
        minX = Math.min(minX, x - 0.8);
        maxX = Math.max(maxX, x + 0.8);
        minY = Math.min(minY, y - 0.6);
        maxY = Math.max(maxY, y + 0.6);
        minZ = Math.min(minZ, z - 0.5);
        maxZ = Math.max(maxZ, z + 0.5);
      });

      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;
      const centerZ = (minZ + maxZ) / 2;

      const sizeX = maxX - minX;
      const sizeY = maxY - minY;
      const size = Math.max(sizeX, sizeY, 5);

      // Update camera and controls
      controlsRef.current.target.set(centerX, centerY, centerZ);
      cameraRef.current.position.z = centerZ + size;
    }
  });

  return (
    <>
      <PerspectiveCamera ref={cameraRef} makeDefault position={[0, 0, 8]} />
      <OrbitControls ref={controlsRef} enableDamping dampingFactor={0.05} autoRotate={false} />

      {/* Lighting */}
      <ambientLight intensity={0.6} />
      <pointLight position={[10, 10, 10]} intensity={1} />
      <pointLight position={[-10, -10, 5]} intensity={0.5} color="#FF6B6B" />

      {/* Grid */}
      <gridHelper args={[20, 20]} position={[0, -2, 0]} />

      {/* Layer blobs */}
      {layers.map((layer) => (
        <BlobNode
          key={layer.id}
          layer={layer}
          isSelected={selectedLayerId === layer.id}
          onSelect={() => onSelectLayer(layer.id)}
          onDragStart={() => {}}
        />
      ))}

      {/* Connection visualization */}
      {layers.map((toLayer) =>
        (toLayer.inputs || []).map((fromId) => {
          // Handle "input" as special case
          if (fromId === 'input') {
            return (
              <line key={`input-${toLayer.id}`}>
                <lineBasicMaterial color="#8B5CF6" linewidth={2} />
                <BufferGeometryLine
                  start={[0, 0, 0]}
                  end={[toLayer.position.x / 100, toLayer.position.y / 100, toLayer.position.z]}
                />
              </line>
            );
          }

          const fromLayer = layers.find(l => l.id === fromId);
          if (!fromLayer) return null;

          return (
            <line key={`${fromId}-${toLayer.id}`}>
              <lineBasicMaterial color="#8B5CF6" linewidth={2} />
              <BufferGeometryLine
                start={[fromLayer.position.x / 100, fromLayer.position.y / 100, fromLayer.position.z]}
                end={[toLayer.position.x / 100, toLayer.position.y / 100, toLayer.position.z]}
              />
            </line>
          );
        })
      )}
    </>
  );
};

// Main component
export const Blob3DView: React.FC = () => {
  const { layers, selectedLayerId, selectLayer, addLayer } = useModelBuilder();
  const containerRef = useRef<HTMLDivElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const layerType = e.dataTransfer.getData('layerType');

    if (layerType) {
      // Import here to avoid circular dependency
      const { LAYER_TEMPLATES, createLayerFromTemplate } = require('../../utils/layerUtils');
      const template = LAYER_TEMPLATES[layerType];

      if (template) {
        // Get input shape from previous layer if exists
        let inputShape: number[] = [];
        let inputs: string[] = ['input'];
        if (layers.length > 0) {
          const lastLayer = layers[layers.length - 1];
          inputShape = lastLayer.outputShape || [];
          inputs = [lastLayer.id];
        }

        // Create layer at random position in 3D space
        const newLayer = createLayerFromTemplate(
          template,
          {
            x: (Math.random() - 0.5) * 400,
            y: (Math.random() - 0.5) * 300,
            z: 0
          },
          inputShape,
          inputs
        );

        addLayer(newLayer);
        selectLayer(newLayer.id);
      }
    }
  };

  return (
    <div
      ref={containerRef}
      className="blob-3d-container"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className="canvas-3d-wrapper">
        <Canvas className="canvas-3d">
          <Scene
            layers={layers}
            selectedLayerId={selectedLayerId}
            onSelectLayer={selectLayer}
          />
        </Canvas>
      </div>

      {/* Controls overlay */}
      <div className="blob-3d-controls">
        <div className="control-info">
          <p>🖱️ Drag to rotate | 🔍 Scroll to zoom | 📋 Drag layers from palette</p>
          <p className="layer-count">Layers: {layers.length}</p>
        </div>
      </div>

      {/* Legend */}
      <div className="blob-3d-legend">
        <h4>Layer Types</h4>
        <div className="legend-items">
          {Object.entries(LAYER_COLORS).map(([type, color]) => (
            <div key={type} className="legend-item">
              <div
                className="legend-color"
                style={{ backgroundColor: color }}
              />
              <span>{type}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Blob3DView;
